import React, { PropsWithChildren, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge, makeStyles, Tooltip } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
// import SchoolIcon from '@material-ui/icons/School';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
// import { MyGroupsSidebarItem } from '@backstage/plugin-org';
// import GroupIcon from '@material-ui/icons/People';
import CategoryIcon from '@material-ui/icons/Category';
import LayersIcon from '@material-ui/icons/Layers';
import LoopIcon from '@mui/icons-material/Loop';
import { LibraryBooks } from '@material-ui/icons';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import AssessmentIcon from '@material-ui/icons/Assessment';
// import { AnsibleLogo } from '@internal/plugin-react-ansible-rhaap';
import { GitHubIcon } from '@backstage/core-components';
import { useRecertificationDueCount } from '@internal/plugin-react-nestle-recertification';

// Set to true when the Recertification feature is ready for users
const RECERTIFICATION_ENABLED = true;

const useDisabledSidebarItemStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    height: 48,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2),
    opacity: 0.4,
    cursor: 'not-allowed',
    color: 'inherit',
    userSelect: 'none',
  },
  icon: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  text: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: 0.5,
  },
}));

const DisabledSidebarItem = ({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) => {
  const classes = useDisabledSidebarItemStyles();
  const { isOpen } = useSidebarOpenState();
  return (
    <Tooltip title="In development" placement="right" arrow>
      <div className={classes.root}>
        <Icon className={classes.icon} />
        {isOpen && <span className={classes.text}>{text}</span>}
      </div>
    </Tooltip>
  );
};

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const RecertificationSidebarItem = () => {
  const { pathname } = useLocation();
  // Refetch whenever the user lands back on the list, e.g. after recertifying.
  const count = useRecertificationDueCount(pathname === '/recertification');

  const Icon = useMemo(
    () => (props: React.ComponentProps<typeof VerifiedUserIcon>) => (
      <Badge
        badgeContent={count}
        color="error"
        max={99}
        overlap="circular"
      >
        <VerifiedUserIcon {...props} />
      </Badge>
    ),
    [count],
  );

  return <SidebarItem icon={Icon} to="recertification" text="Recertification" />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        {/* <SidebarItem icon={SchoolIcon} to="onboarding" text="Onboarding" /> */}
        <SidebarItem
          icon={LayersIcon}
          to="catalog?filters[kind]=system&filters[user]=owned&limit=20"
          text="My Systems"
        />
        <div data-onboarding="sidebar-catalog">
          <SidebarItem
            icon={CategoryIcon}
            to="catalog?filters[kind]=component"
            text="Catalog"
          />
        </div>
        <SidebarItem
          icon={GitHubIcon}
          to="github-codespaces"
          text="Codespaces"
        />
        <SidebarItem icon={LibraryBooks} to="docs" text="TechDocs" />
        <SidebarItem
          icon={CreateComponentIcon}
          to="create?filters[kind]=template&filters[tags]=end-user&filters[user]=all&limit=20"
          text="Create..."
        />
        {/* End global nav */}
        <SidebarDivider />
        {RECERTIFICATION_ENABLED ? (
          <RecertificationSidebarItem />
        ) : (
          <DisabledSidebarItem icon={VerifiedUserIcon} text="Recertification" />
        )}
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarItem icon={LoopIcon} to="create/tasks" text="Tasks" />
      <SidebarItem icon={AssessmentIcon} to="template-usage" text="Template Usage" />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
